/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useEffect, useRef, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useLocation, useNavigate } from 'react-router';
import { html } from 'lit/static-html.js';
import {
  Button,
  DataTable,
  DataTableWC,
  Grid,
  GridCell,
  Icon,
  PageSection,
  Search,
  Select,
  SelectOption,
  Spacer,
  Stack,
  TextTitle,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';
// import { formatTimeWithTimezone } from '@pypestream/utils';

import { useCreateSolutionModal } from '../components';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
  useUserCtxSelector,
} from '../xstate/app.xstate';
import { WithLoadingState } from '../router';
import { StudioPageProps, useProStudioViewerRole } from '../hooks';
import { useBobcatFlag } from '../hooks/use-bobcat-flag';
import { buildSolutionHref, isPlainLeftClick } from '../utils/solution-link';
import { HomeLanding, StartPick } from './home-start';
import { StepByStepForm } from './home-step-by-step';
import { BlankSolutionForm } from './home-blank';
import { clearPendingSetupCopy, peekPendingSetupCopy } from './setup-copy';
import { TalkItThrough } from './talk/talk-it-through';
import { useSolutionCardFacts } from './home-solution-facts';
import {
  dismissedSelectionReset,
  shouldRevealFailedSelection,
  solutionSelectEvent,
} from '../utils/solution-open-failure';

interface HardcodedSolutionType {
  id?: string;
  name: string;
  first_name: string;
  last_name: string;
  profilePhoto: string;
  updated: string;
}

type HomePageProps = StudioPageProps;

const HomePage: React.FC<HomePageProps> = () => {
  const tableRef = useRef<DataTableWC>(null);
  const lastSelectedId = useRef<string | undefined>(undefined);

  const {
    bots,
    projectId,
    projectName,
    uniqueCustomerId,
    userProjects,
    cardSolutions,
    openFailureId,
  } = useProStudioCtxSelector((ctx) => ({
    projectId: ctx.project?.id,
    projectName: ctx.project?.name,
    uniqueCustomerId: ctx.project?.projectProStudioSetting?.customerId,
    openFailureId: ctx.solutionOpenFailure?.solutionId,
    cardSolutions: (ctx.solutions || []).flatMap((s) =>
      s.id ? [{ id: s.id, botSolutionId: s.botSolutionId }] : []
    ),
    bots: (ctx.solutions || [])
      .map((bot) => ({
        id: bot.id,
        name: bot.displayName,
        // HARDCODED DATA FOR NOW
        first_name: 'Firstname',
        last_name: 'Lastname',
        profilePhoto:
          'https://images.pexels.com/photos/2216542/pexels-photo-2216542.jpeg?auto=compress&amp;cs=tinysrgb&amp;w=100&amp;h=100&amp;dpr=2',
        updated: '2025-01-14T13:13:17.986Z',
      }))
      // @todo - move this sorting to gql query, currently we don't have ability to add sorting to the getSolutions query so doing it here
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    userProjects: ctx.userProjects,
  }));

  const openFailureDismissed = useProStudioCtxSelector(
    (ctx) => ctx.solutionOpenFailureDismissed === true
  );
  const isViewer = useProStudioViewerRole();
  const firstName = useUserCtxSelector((ctx) => ctx.user?.firstName);

  useEffect(() => {
    const table = tableRef.current?.table;
    const selectedId = table?.getSelectedRowModel().rows[0]?.id;
    const reset = dismissedSelectionReset({
      dismissed: openFailureDismissed,
      tableReady: !!table,
      rowSelected: !!selectedId,
    });

    if (reset === 'forget') {
      lastSelectedId.current = undefined;
    }

    if (reset === 'reset') {
      table?.resetRowSelection();
    }
  }, [openFailureDismissed]);

  const { openModal, createSolutionModal } = useCreateSolutionModal();
  // Project Bobcat, gated: a brand-new entry point on a page every user
  // sees, off by default. `pending` (LaunchDarkly not yet answered) reads as
  // not-allowed here rather than flashing the button on then off.
  const conversationalCreateEnabled =
    useBobcatFlag('conversationalCreate') === 'allowed';
  const [t] = useTranslation();
  const navigate = useNavigate();
  const isIdle = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded.idle'
  );
  // PSTUDIO-3739: when the open solution is deleted (e.g. swept by concurrent
  // E2E), the machine ejects to the parked terminal state `solutionNotFound` and
  // the router redirects here to All Solutions — but the machine stays parked
  // there (NOT `projectDataLoaded.idle`). This page's actions (Create Solution)
  // must stay enabled in that parked state too, otherwise a delete→redirect
  // leaves the "Create Solution" button permanently disabled and the user stuck.
  const isSolutionNotFound = useProStudioStateMatches('solutionNotFound');
  const createDisabled = !isIdle && !isSolutionNotFound;

  // Project Bobcat (bobcat-conversational-create): the project's front door
  // — the solutions as cards beside Create, "How do you want to start?", the
  // step-by-step setup — before the solutions table (see `home-start.tsx`).
  // `null` is the
  // default: the landing when there are solutions, Start when there are none.
  // Viewers can't create, so they go straight to the table as before.
  // "Copy as a new solution" (Design's Setup) lands here with a setup to
  // start the step-by-step form from.
  const [setupCopy] = useState(peekPendingSetupCopy);
  const [startView, setStartView] = useState<
    'landing' | 'create' | 'talk' | 'steps' | 'blank' | 'solutions' | null
  >(setupCopy ? 'steps' : null);
  // The front door's steps are this page's own state, so a link back to this
  // same route ("All Agents" in the breadcrumb, while on Create) changed
  // nothing. Every navigation gets a new location key: go back to the
  // default view on any after the first.
  const { key: locationKey } = useLocation();
  const firstLocationKey = useRef(locationKey);
  useEffect(() => {
    if (locationKey !== firstLocationKey.current) setStartView(null);
  }, [locationKey]);
  const frontDoor = conversationalCreateEnabled && !isViewer;
  const view = !frontDoor
    ? 'solutions'
    : (startView ?? (bots.length ? 'landing' : 'create'));
  const cardFacts = useSolutionCardFacts({
    solutions: cardSolutions,
    projectId,
    uniqueCustomerId,
    enabled: view === 'landing',
  });

  const columns = useMemo<ColumnDef<HardcodedSolutionType>[]>(
    () => [
      {
        accessorKey: 'name',
        accessorFn: (row) => row.name,
        header: () =>
          t('pro-studio/homepage:table.column1', {
            defaultValue: 'Solution Title',
          }) || '',
        cell: ({ row }) => {
          const href = buildSolutionHref(projectId ?? '', row.original.id);
          return html` <a
            href=${href}
            style="display:block;color:inherit;text-decoration:none;"
            @click=${(e: MouseEvent) => {
              // Only a plain left-click drives SPA navigation. Any modified click (cmd/ctrl/shift/
              // alt) must reach the browser's native new-tab/new-window handling, so we stop it
              // from bubbling to the row @click (which would SPA-navigate the CURRENT tab) but must
              // NOT preventDefault. Real middle/right-clicks fire auxclick/contextmenu (never
              // click), so they never reach this handler — the native <a href> opens them itself.
              if (!isPlainLeftClick(e)) {
                e.stopPropagation();
                return;
              }
              // Plain left-click → block the anchor's own navigation; the existing ps-table-row
              // @click (toggleSelected → selectBot → navigate) drives SPA navigation.
              e.preventDefault();
            }}
          >
            <ps-text-subtitle size="small"
              >${row?.original?.name}</ps-text-subtitle
            >
          </a>`;
        },
      },
      // {
      //   accessorKey: 'userName',
      //   header: () =>
      //     t('pro-studio/homepage:table.column2', {
      //       defaultValue: 'Last updated',
      //     }) || '',
      //   cell: ({ row }) => {
      //     if (!row || !row?.original) {
      //       return null;
      //     }

      //     const { first_name, last_name, profilePhoto, updated } = row.original;
      //     const userName = `${first_name} ${last_name}`;

      //     return html`<ps-stack
      //       alignItems="center"
      //       justifyContent="end"
      //       gutter="xsmall"
      //     >
      //       <ps-text-body>
      //         ${formatTimeWithTimezone({
      //           fromDate: updated,
      //           // locale: dateFnsLocales[defaultLanguage || fallbackLanguage],
      //           // userTimeZone,
      //         })}
      //         ${' '}by
      //       </ps-text-body>
      //       <ps-tooltip content=${userName}>
      //         <ps-avatar
      //           size="small"
      //           src=${profilePhoto}
      //           label=${userName}
      //         ></ps-avatar>
      //       </ps-tooltip>
      //     </ps-stack>`;
      //   },
      //   meta: {
      //     align: 'end',
      //   },
      // },
    ],
    [projectId, t]
  );

  if (projectId && view === 'landing') {
    return (
      <HomeLanding
        firstName={firstName}
        projectName={projectName}
        solutions={bots.flatMap((b) =>
          b.id ? [{ id: b.id, name: b.name || '' }] : []
        )}
        facts={cardFacts}
        createDisabled={createDisabled}
        onCreate={() => setStartView('create')}
        onOpen={(solution) =>
          sendProStudioEvent({
            type: 'selectBot',
            name: solution.name,
            id: solution.id,
            cb: (path?: string) => navigate(path || ''),
          })
        }
      />
    );
  }
  if (projectId && view === 'create') {
    return (
      <>
        <StartPick
          firstSolution={!bots.length}
          disabled={createDisabled}
          onBack={bots.length ? () => setStartView('landing') : undefined}
          onTalk={() => setStartView('talk')}
          onSteps={() => setStartView('steps')}
          onBlank={() => setStartView('blank')}
        />
        {createSolutionModal}
      </>
    );
  }
  if (projectId && view === 'talk') {
    return (
      <TalkItThrough
        firstName={firstName}
        onBack={() => {
          clearPendingSetupCopy();
          setStartView('create');
        }}
      />
    );
  }
  if (projectId && view === 'blank') {
    return <BlankSolutionForm onBack={() => setStartView('create')} />;
  }
  if (projectId && view === 'steps') {
    return (
      <StepByStepForm
        initial={setupCopy}
        onBack={() => {
          clearPendingSetupCopy();
          setStartView('create');
        }}
      />
    );
  }

  return projectId ? (
    <React.Fragment>
      <PageSection test-id="ps-homepage-page-section">
        {frontDoor && (
          <>
            <Button
              size="small"
              variant="ghost"
              test-id="bobcat-home-back"
              onClick={() => setStartView('landing')}
            >
              <Icon slot="prefix" name="arrow-left" size="xsmall" />
              Home
            </Button>
            <Spacer size="small" />
          </>
        )}
        <Grid alignItems="center">
          <GridCell xsmall="12" large="5">
            <TextTitle test-id="ps-homepage-title" size="xsmall">
              <TranslationComponent i18nKey="pro-studio/homepage:title">
                Solutions
              </TranslationComponent>
            </TextTitle>
          </GridCell>
          <GridCell xsmall="12" large="7">
            <Stack alignItems="center" justifyContent="end">
              <Search
                test-id="ps-homepage-search"
                size="large"
                name="search"
                style={{ width: 319 }}
                onInput={(e) => {
                  tableRef.current?.table.setGlobalFilter(
                    String(e.target?.value) || ''
                  );
                }}
              />
              {frontDoor && (
                <Button
                  test-id="bobcat-home-create-agentic"
                  size="large"
                  onClick={() => setStartView('create')}
                  disabled={createDisabled}
                >
                  <Icon name="add" slot="prefix" />
                  Create an agentic solution
                </Button>
              )}
              {!isViewer && !frontDoor && (
                <Button
                  test-id="ps-homepage-create-solution-btn"
                  size="large"
                  onClick={openModal}
                  disabled={!isIdle && !isSolutionNotFound}
                >
                  <Icon name="add" slot="prefix" />
                  <TranslationComponent i18nKey="pro-studio/homepage:create">
                    Create Solution
                  </TranslationComponent>
                </Button>
              )}
            </Stack>
          </GridCell>
        </Grid>

        <Spacer size="large" />

        <DataTable
          test-id="ps-homepage-solutions-table"
          ref={tableRef}
          filterColumns={['name']}
          size="small"
          // @ts-ignore
          columns={columns}
          data={bots}
          enable-row-selection
          onRowSelectionChange={(e: CustomEvent) => {
            const requestedId = e.detail.selected[0]?.original.id as
              | string
              | undefined;
            const requestedName = e.detail.selected[0]?.original.name as
              | string
              | undefined;
            const previousId = lastSelectedId.current;
            lastSelectedId.current = requestedId;

            const event = solutionSelectEvent({
              requestedId,
              requestedName,
              openFailureId,
            });

            if (event) {
              sendProStudioEvent({
                ...event,
                cb: (path?: string) => navigate(path || ''),
              });
              return;
            }

            if (
              requestedId &&
              requestedName &&
              shouldRevealFailedSelection({
                previousId,
                requestedId,
                openFailureId,
              })
            ) {
              sendProStudioEvent({
                type: 'selectBot',
                id: requestedId,
                name: requestedName,
                cb: (path?: string) => navigate(path || ''),
              });
            }
          }}
        />
      </PageSection>
      {createSolutionModal}
    </React.Fragment>
  ) : (
    <Stack
      gutter="xsmall"
      justifyContent="center"
      alignItems="center"
      direction="column"
    >
      <TextTitle size="xsmall">
        <TranslationComponent i18nKey="pro-studio/homepage:vocabulary.no_project_selected">
          Please select a project to view the available solutions
        </TranslationComponent>
      </TextTitle>
      <Spacer size="xsmall" />
      <Select
        placeholder="Select a project"
        onChange={(e) => {
          const selectedProjectId = e.target.value as string;
          sendProStudioEvent({
            type: 'setParams',
            payload: {
              projectId: selectedProjectId,
            },
          });
        }}
      >
        {userProjects?.map((project) => (
          <SelectOption key={project.id} value={project.id}>
            {project.name}
          </SelectOption>
        ))}
      </Select>
    </Stack>
  );
};

export const Home = WithLoadingState(HomePage);
